// Task types and workflows (templates) as the admin web sees them (docs/12, docs/03). The graph
// and the requirements travel as plain JSON in the OpenAPI spec, so their shapes are written here
// from docs/12 "The graph" and "Requirements"; the API refuses unknown fields, so nothing beyond
// these goes into a saved definition.
import type { Schemas } from "../api/client";
import type { WorkflowStats } from "../ratings/model";

export type TaskType = Schemas["TaskTypeDto"];
export type TaskTypeInput = Schemas["TaskTypeBody"];
export type VersionDto = Schemas["VersionDto"];
export type VersionSummary = Schemas["VersionSummaryDto"];
export type Issue = Schemas["IssueDto"];

export const TEMPLATE_STATUSES = ["draft", "active", "disabled", "archived"] as const;
export type TemplateStatus = (typeof TEMPLATE_STATUSES)[number];

/** `WorkflowDto` of the catalog API (the spec's schema name collides with the legacy workflow's). */
export interface WorkflowSummary {
  id: string;
  slug: string;
  name: string;
  description: string;
  task_type_id: string;
  icon: string | null;
  status: TemplateStatus;
  created_by: string;
  created_at: number;
  updated_at: number;
  /** The newest published version number. */
  latest_version: number | null;
  /** The draft's version number, when a draft exists. */
  draft_version: number | null;
  /** Usage and ratings over all versions (D73). */
  stats: WorkflowStats;
}

export interface WorkflowInput {
  slug: string;
  name: string;
  description: string;
  task_type_id: string;
  icon: string | null;
}

// --- the graph (D54) ---------------------------------------------------------------------------

/** How a prompt step ended: its run's outcome, or `skipped` when its pre-execution script exited 3. */
export type StepOutcome = "succeeded" | "failed" | "timed_out" | "skipped";
export const STEP_OUTCOMES: StepOutcome[] = ["succeeded", "failed", "timed_out", "skipped"];

/** A broker tool a prompt step may use, in capability-token form (docs/05). */
export interface ToolCapability {
  tool: string;
  actions: string[];
  requires_approval?: boolean;
}

export type ScriptLanguage = "python" | "bash";

/** Run in the worker before the agent (D63): exit 0 runs the step, 3 skips it, anything else runs it anyway. */
export interface PreExecution {
  language: ScriptLanguage;
  source: string;
  timeout_seconds?: number;
  /** Broker actions the script may call with its own token (D77). */
  broker?: BrokerGrant[];
}

/** One broker action a pre-execution script may call, with the credential requirement it uses (by name). */
export interface BrokerGrant {
  tool: string;
  action: string;
  requirement: string;
}

/** The agent CLI (D67); only Claude runs today. */
export type AgentCli = "claude" | "cursor";

export interface PromptNode {
  kind: "prompt";
  id: string;
  name?: string;
  prompt: string;
  model?: string;
  timeout_minutes?: number;
  needs_docker?: boolean;
  max_visits?: number;
  tools?: ToolCapability[];
  outputs?: string[];
  cli?: AgentCli;
  pre_execution?: PreExecution;
  /** Reserved; the API refuses it until post-execution scripts are built. */
  post_execution?: unknown;
}

export interface ApprovalNode {
  kind: "approval";
  id: string;
  name?: string;
  question: string;
  options: string[];
  max_visits?: number;
}

export type EndResult = "succeeded" | "failed";

export interface EndNode {
  kind: "end";
  id: string;
  name?: string;
  result: EndResult;
}

/** A parent task runs its approved plan's subtasks (D87); it exits with `subtasks` = `done` | `failed`. */
export interface OrchestrateNode {
  kind: "orchestrate";
  id: string;
  name?: string;
  /** At most this many subtasks running at once; the group's `max_parallel_tasks` when absent. */
  max_parallel?: number;
  max_visits?: number;
}

export type GraphNode = PromptNode | ApprovalNode | OrchestrateNode | EndNode;
export type NodeKind = GraphNode["kind"];

export type OutputTest =
  | { key: string; equals: string }
  | { key: string; in: string[] }
  | { key: string; exists: boolean };

export type Condition =
  | { outcome: StepOutcome }
  | { output: OutputTest }
  | { answer: string }
  | "otherwise";

export interface GraphEdge {
  from: string;
  to: string;
  when: Condition;
}

export interface Graph {
  start: string;
  nodes: GraphNode[];
  edges: GraphEdge[];
}

// --- requirements (D55) ------------------------------------------------------------------------

/** Credential kinds a requirement may name; `anthropic` is either Anthropic kind. */
export const CREDENTIAL_CHOICES = [
  "anthropic",
  "anthropic_api_key",
  "anthropic_oauth_token",
  "github_pat",
  "github_app",
  "jira_token",
  "slack_bot",
  "db",
  "aws_sso",
  "vpn_profile",
  "generic",
  "cursor_api_key",
] as const;
export type CredentialChoice = (typeof CREDENTIAL_CHOICES)[number];

export interface CredentialRequirement {
  kind: "credential";
  /** What a pre-execution script's broker grant calls it (D77). */
  name?: string;
  credential_kind: CredentialChoice;
  required: boolean;
  platform_default?: boolean;
}

export interface OauthRequirement {
  kind: "oauth";
  provider: string;
  required: boolean;
}

export interface IsolationRequirement {
  kind: "isolation";
  level: "vm";
}

/** Toolchains the platform installs in a group's own worker image (D90). */
export const TOOLCHAINS = ["rust", "go", "java", "bazel"] as const;
export type Toolchain = (typeof TOOLCHAINS)[number];

/** The group's worker image must have this toolchain (D90). */
export interface ToolchainRequirement {
  kind: "toolchain";
  toolchain: Toolchain;
}

export type Requirement = CredentialRequirement | OauthRequirement | IsolationRequirement | ToolchainRequirement;

/** What a draft save sends and an editor edits: the graph, the requirements and the attached skill ids. */
export interface VersionDocument {
  definition: Graph;
  requirements: Requirement[];
  skills: string[];
}

export const EMPTY_GRAPH: Graph = { start: "", nodes: [], edges: [] };
